import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { adminApi } from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import AdminSidebar from '../../components/sidebar/AdminSidebar';
import ApprovalWorkflowDrawer from '../../components/dashboard/ApprovalWorkflowDrawer';
import './UserManagementPage.css';

interface User {
  _id: string;
  name: string;
  email: string;
  isVerified: boolean;
  isAdmin: boolean;
  accountStatus: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  companyName?: string;
  phone?: string;
}

const UserManagementPage: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] =
    useState<'all' | 'verified' | 'unverified' | 'admin'>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [accountReviewUser, setAccountReviewUser] = useState<User | null>(null);

  useEffect(() => {
    if (currentUser) fetchUsers(); // prevent early call before auth loads
  }, [currentUser]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getAllUsers();
      setUsers(response.users || []);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      filter === 'all' ||
      (filter === 'verified' && user.isVerified) ||
      (filter === 'unverified' && !user.isVerified) ||
      (filter === 'admin' && user.isAdmin);

    return matchesSearch && matchesFilter;
  });

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleDeleteUser = async (userId: string) => {
    if (
      !window.confirm(
        'Are you sure you want to delete this user? This action cannot be undone.'
      )
    ) {
      return;
    }

    try {
      await adminApi.deleteUser(userId);
      setUsers(users.filter((user) => user._id !== userId));
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleUpdateUser = async (userData: Partial<User>) => {
    if (!selectedUser) return;

    try {
      await adminApi.updateUser(selectedUser._id, userData);
      setUsers(
        users.map((user) =>
          user._id === selectedUser._id ? { ...user, ...userData } : user
        )
      );
      setShowEditModal(false);
      setSelectedUser(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to update user');
    }
  };

  const handleApproveAccount = async (userId: string) => {
    try {
      await adminApi.approveAccount(userId);
      setUsers(
        users.map((user) =>
          user._id === userId ? { ...user, accountStatus: 'approved' } : user
        )
      );
      setAccountReviewUser(null);
      alert('Account approved successfully!');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to approve account');
    }
  };

  const handleRejectAccount = async (userId: string, reason: string) => {
    try {
      await adminApi.rejectAccount(userId, reason.trim());
      setUsers(
        users.map((user) =>
          user._id === userId ? { ...user, accountStatus: 'rejected' } : user
        )
      );
      setAccountReviewUser(null);
      alert('Account rejected successfully.');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to reject account');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <DashboardLayout sidebar={<AdminSidebar />}>
        <div className="user-management-loading">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout sidebar={<AdminSidebar />}>
      <div className="user-management-page">
        <div className="page-header">
          <h2>User Management</h2>
          <p>Manage all registered users in the system</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="filters-section">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter-buttons">
            <button
              className={filter === 'all' ? 'active' : ''}
              onClick={() => setFilter('all')}
            >
              All ({users.length})
            </button>
            <button
              className={filter === 'verified' ? 'active' : ''}
              onClick={() => setFilter('verified')}
            >
              Verified ({users.filter((u) => u.isVerified).length})
            </button>
            <button
              className={filter === 'unverified' ? 'active' : ''}
              onClick={() => setFilter('unverified')}
            >
              Unverified ({users.filter((u) => !u.isVerified).length})
            </button>
            <button
              className={filter === 'admin' ? 'active' : ''}
              onClick={() => setFilter('admin')}
            >
              Admins ({users.filter((u) => u.isAdmin).length})
            </button>
          </div>
        </div>

        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Company</th>
                <th>Verification</th>
                <th>Account Status</th>
                <th>Role</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user._id}>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>{user.companyName || '-'}</td>

                  <td>
                    <span
                      className={`status-badge ${
                        user.isVerified ? 'verified' : 'unverified'
                      }`}
                    >
                      {user.isVerified ? 'Verified' : 'Unverified'}
                    </span>
                  </td>

                  <td>
                    <span
                      className={`account-status-badge ${user.accountStatus}`}
                    >
                      {user.accountStatus.charAt(0).toUpperCase() + user.accountStatus.slice(1)}
                    </span>
                  </td>

                  <td>
                    <span
                      className={`role-badge ${
                        user.isAdmin ? 'admin' : 'user'
                      }`}
                    >
                      {user.isAdmin ? 'Admin' : 'User'}
                    </span>
                  </td>

                  <td>{formatDate(user.createdAt)}</td>

                  <td>
                    <div className="action-buttons">
                      {user.accountStatus === 'pending' && (
                        <>
                          <button
                            className="approve-btn"
                            onClick={() => setAccountReviewUser(user)}
                            disabled={user._id === currentUser?.id}
                          >
                            Review in Drawer
                          </button>
                        </>
                      )}
                      <button
                        className="edit-btn"
                        onClick={() => handleEditUser(user)}
                        disabled={user._id === currentUser?.id}
                      >
                        Edit
                      </button>

                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteUser(user._id)}
                        disabled={user.isAdmin || user._id === currentUser?.id}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredUsers.length === 0 && (
            <div className="no-users">
              <p>No users found matching your criteria.</p>
            </div>
          )}
        </div>

        <div className="users-mobile-list">
          {filteredUsers.length === 0 ? (
            <div className="no-users">
              <p>No users found matching your criteria.</p>
            </div>
          ) : (
            filteredUsers.map((user) => (
              <article key={`mobile-${user._id}`} className="user-mobile-card">
                <div className="user-mobile-head">
                  <div>
                    <h4>{user.name}</h4>
                    <p>{user.email}</p>
                  </div>
                  <span className={`role-badge ${user.isAdmin ? 'admin' : 'user'}`}>
                    {user.isAdmin ? 'Admin' : 'User'}
                  </span>
                </div>

                <div className="user-mobile-meta">
                  <div><strong>Company:</strong> {user.companyName || '-'}</div>
                  <div><strong>Joined:</strong> {formatDate(user.createdAt)}</div>
                  <div>
                    <strong>Verification:</strong>{' '}
                    <span className={`status-badge ${user.isVerified ? 'verified' : 'unverified'}`}>
                      {user.isVerified ? 'Verified' : 'Unverified'}
                    </span>
                  </div>
                  <div>
                    <strong>Account:</strong>{' '}
                    <span className={`account-status-badge ${user.accountStatus}`}>
                      {user.accountStatus.charAt(0).toUpperCase() + user.accountStatus.slice(1)}
                    </span>
                  </div>
                </div>

                <div className="action-buttons user-mobile-actions">
                  {user.accountStatus === 'pending' && (
                    <>
                      <button
                        className="approve-btn"
                        onClick={() => setAccountReviewUser(user)}
                        disabled={user._id === currentUser?.id}
                      >
                        Review in Drawer
                      </button>
                    </>
                  )}
                  <button
                    className="edit-btn"
                    onClick={() => handleEditUser(user)}
                    disabled={user._id === currentUser?.id}
                  >
                    Edit
                  </button>
                  <button
                    className="delete-btn"
                    onClick={() => handleDeleteUser(user._id)}
                    disabled={user.isAdmin || user._id === currentUser?.id}
                  >
                    Delete
                  </button>
                </div>
              </article>
            ))
          )}
        </div>

        {showEditModal && selectedUser && (
          <EditUserModal
            user={selectedUser}
            onClose={() => {
              setShowEditModal(false);
              setSelectedUser(null);
            }}
            onSave={handleUpdateUser}
          />
        )}

        <ApprovalWorkflowDrawer
          isOpen={Boolean(accountReviewUser)}
          title="Account Approval Decision"
          subtitle="Approve or reject user onboarding with documented reason."
          onClose={() => setAccountReviewUser(null)}
          contextItems={
            accountReviewUser
              ? [
                  { label: 'Name', value: accountReviewUser.name },
                  { label: 'Email', value: accountReviewUser.email },
                  { label: 'Company', value: accountReviewUser.companyName || '-' },
                  { label: 'Joined', value: formatDate(accountReviewUser.createdAt) },
                ]
              : []
          }
          onApprove={
            accountReviewUser
              ? () => handleApproveAccount(accountReviewUser._id)
              : undefined
          }
          onReject={
            accountReviewUser
              ? (reason) => handleRejectAccount(accountReviewUser._id, reason)
              : undefined
          }
          rejectionLabel="Onboarding Rejection Reason"
          rejectionPlaceholder="Describe why this account failed onboarding checks."
        />
      </div>
    </DashboardLayout>
  );
};

// =======================
// EDIT USER MODAL
 // =======================

interface EditUserModalProps {
  user: User;
  onClose: () => void;
  onSave: (userData: Partial<User>) => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  user,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    name: user.name,
    email: user.email,
    isVerified: user.isVerified,
    isAdmin: user.isAdmin,
    companyName: user.companyName || '',
    phone: user.phone || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Edit User</h3>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="edit-user-form">
          <div className="form-row">
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                aria-label="User name"
                title="Enter user name"
              />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
                aria-label="User email"
                title="Enter user email"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Company Name</label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) =>
                  handleInputChange('companyName', e.target.value)
                }
                aria-label="Company name"
                title="Enter company name"
              />
            </div>

            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                aria-label="Phone number"
                title="Enter phone number"
              />
            </div>
          </div>

          <div className="form-row checkboxes">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.isVerified}
                onChange={(e) =>
                  handleInputChange('isVerified', e.target.checked)
                }
              />
              Verified Account
            </label>

            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.isAdmin}
                onChange={(e) =>
                  handleInputChange('isAdmin', e.target.checked)
                }
              />
              Admin Privileges
            </label>
          </div>

          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="save-btn">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserManagementPage;
